import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle2,
  ExternalLink,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Trash2,
  Users,
  X,
  AlertTriangle,
  KeyRound,
} from "lucide-react";
import { BACKEND_ORIGIN, api, getToken } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { SyncControls } from "@/components/shared/SyncControls";
import { PLATFORM_ICONS } from "@/components/shared/PlatformIcons";
import { AccountGroupsCard } from "@/pages/platforms/AccountGroupsCard";

interface ConnectedAccount {
  account_id: string;
  platform: string;
  account_type: string;
  display_name: string;
  handle?: string | null;
  picture_url?: string | null;
  connected_at: string;
  is_oauth?: boolean;
  last_synced_at?: string | null;
  last_error?: string | null;
  needs_reconnect?: boolean;
}

interface CatalogItem {
  id: string;
  platform: string;
  account_type: string;
  title: string;
  cta: string;
  description: string;
  status: "live" | "coming_soon" | "deprecated";
  region: string;
  group: string;
  reason?: string;
  start_path?: string;
  auth_method?: "app_password" | "bot_token";
  connected_accounts: ConnectedAccount[];
  connected_count: number;
}

interface Catalog {
  items: CatalogItem[];
  counter: { connected: number; limit: number };
}

const STATUS_BADGE: Record<string, string> = {
  live: "bg-emerald-50 text-emerald-700 border-emerald-200",
  coming_soon: "bg-amber-50 text-amber-700 border-amber-200",
  deprecated: "bg-rose-50 text-rose-700 border-rose-200",
};

const GROUP_LABEL: Record<string, string> = {
  meta: "Meta",
  google: "Google",
  western: "Western social",
  messaging: "Messaging",
  regional: "Regional",
};

/** Opens the OAuth popup and refreshes catalog when it closes. */
function openOAuthPopup(url: string, onClose: () => void) {
  const w = 640;
  const h = 720;
  const left = window.screen.width / 2 - w / 2;
  const top = window.screen.height / 2 - h / 2;
  const popup = window.open(
    url,
    "sg-oauth",
    `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );
  if (!popup) {
    toast.error("Please allow popups to connect this account.");
    return;
  }
  const timer = setInterval(() => {
    if (popup.closed) {
      clearInterval(timer);
      onClose();
    }
  }, 500);
}

export const Platforms: React.FC = () => {
  const qc = useQueryClient();
  const [customAuth, setCustomAuth] = useState<CatalogItem | null>(null);

  const catalog = useQuery<Catalog>({
    queryKey: ["catalog"],
    queryFn: async () => (await api.get("/platforms/catalog")).data,
  });

  const disconnect = useMutation({
    mutationFn: async (accountId: string) =>
      (await api.delete(`/platforms/${accountId}`)).data,
    onSuccess: () => {
      toast.success("Account disconnected");
      qc.invalidateQueries({ queryKey: ["catalog"] });
      qc.invalidateQueries({ queryKey: ["platforms"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail || "Disconnect failed"),
  });

  const startConnect = (item: CatalogItem) => {
    if (item.status !== "live") {
      if (item.status === "deprecated") {
        toast.error(item.reason || "This account type is deprecated by the platform.");
      } else {
        toast.info(`${item.title} is coming soon — we're building the OAuth flow.`);
      }
      return;
    }
    // Non-OAuth flows (Bluesky app-password, Telegram bot-token) open an in-page modal.
    if (item.auth_method === "app_password" || item.auth_method === "bot_token") {
      setCustomAuth(item);
      return;
    }
    const token = getToken();
    if (!token) {
      toast.error("Please log in first.");
      return;
    }
    const path = item.start_path || `/api/oauth/${item.platform === "youtube" ? "google" : "meta"}/start`;
    // `path` already contains `/api/...` so we use the raw origin (not API_BASE)
    // to avoid producing a double `/api/api/` prefix.
    const url = `${BACKEND_ORIGIN}${path}?token=${encodeURIComponent(token)}`;
    openOAuthPopup(url, () => {
      qc.invalidateQueries({ queryKey: ["catalog"] });
    });
  };

  if (catalog.isLoading) {
    return (
      <DashboardLayout
        title="Connect Account"
        subtitle="Connect every social platform separately — each account counts once toward your subscription limit."
        seoPath="/platforms"
      >
        <div className="sg-skeleton h-96" />
      </DashboardLayout>
    );
  }

  const { items = [], counter } = catalog.data ?? { items: [], counter: { connected: 0, limit: 10 } };
  const grouped = items.reduce<Record<string, CatalogItem[]>>((acc, it) => {
    (acc[it.group] ??= []).push(it);
    return acc;
  }, {});
  const groupOrder = ["meta", "google", "western", "messaging", "regional"];

  const atLimit = counter.limit > 0 && counter.connected >= counter.limit;

  return (
    <DashboardLayout
      title="Connect Account"
      subtitle="Connect every social platform separately — each account counts once toward your subscription limit."
      seoPath="/platforms"
      seoDescription="Connect YouTube, Facebook Pages, Instagram Business/Creator, Threads, LinkedIn, TikTok, X, Pinterest, Bluesky, Tumblr and more — each account type on its own card."
    >
      <AccountGroupsCard />
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div
          data-testid="account-counter"
          className={`sg-chip text-sm px-3 py-1.5 ${
            atLimit ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-100 text-slate-700 border-slate-200"
          }`}
        >
          <Users className="h-3.5 w-3.5 mr-1.5" />
          <span className="font-semibold">{counter.connected}</span>
          <span className="mx-1 opacity-60">/</span>
          <span>{counter.limit}</span>
          <span className="ml-1.5">Accounts Connected</span>
        </div>
        {atLimit && (
          <div className="text-xs text-rose-700 flex items-center gap-1" data-testid="limit-reached">
            <AlertTriangle className="h-3.5 w-3.5" /> Subscription limit reached. Disconnect an account or upgrade.
          </div>
        )}
      </div>

      {groupOrder.map((g) => {
        const cards = grouped[g] || [];
        if (cards.length === 0) return null;
        return (
          <section key={g} className="mb-8" data-testid={`group-${g}`}>
            <div className="sg-label mb-3">{GROUP_LABEL[g] || g}</div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {cards.map((item) => (
                <PlatformCard
                  key={item.id}
                  item={item}
                  onConnect={() => startConnect(item)}
                  onDisconnect={(id) => disconnect.mutate(id)}
                  disconnectPending={disconnect.isPending}
                  atLimit={atLimit && item.connected_count === 0}
                />
              ))}
            </div>
          </section>
        );
      })}

      <CustomAuthDialog
        item={customAuth}
        onClose={() => setCustomAuth(null)}
        onConnected={() => {
          setCustomAuth(null);
          qc.invalidateQueries({ queryKey: ["catalog"] });
        }}
      />
    </DashboardLayout>
  );
};

/* ---------------- Custom auth (Bluesky app-password + Telegram bot-token) ---------------- */

const CustomAuthDialog: React.FC<{
  item: CatalogItem | null;
  onClose: () => void;
  onConnected: () => void;
}> = ({ item, onClose, onConnected }) => {
  const [f1, setF1] = useState("");
  const [f2, setF2] = useState("");
  const connect = useMutation({
    mutationFn: async () => {
      if (!item) return;
      if (item.auth_method === "app_password") {
        return (await api.post("/oauth/generic/bluesky/connect", {
          identifier: f1.trim(),
          app_password: f2.trim(),
        })).data;
      } else {
        return (await api.post("/oauth/generic/telegram/connect", {
          bot_token: f1.trim(),
          chat_id: f2.trim(),
        })).data;
      }
    },
    onSuccess: () => {
      toast.success(`${item?.title} connected!`);
      setF1("");
      setF2("");
      onConnected();
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Connect failed"),
  });

  const isBluesky = item?.auth_method === "app_password";

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid={`custom-auth-${item?.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" /> Connect {item?.title}
          </DialogTitle>
          <DialogDescription>
            {isBluesky ? (
              <>
                Generate an <b>App Password</b> at{" "}
                <a href="https://bsky.app/settings/app-passwords" target="_blank" rel="noopener noreferrer" className="underline">
                  bsky.app/settings/app-passwords
                </a>{" "}
                and paste it below. Never paste your main password.
              </>
            ) : (
              <>
                Create a bot via{" "}
                <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="underline">
                  @BotFather
                </a>{" "}
                to get a bot token, then add the bot as an admin of the channel/group you want to post to.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>{isBluesky ? "Handle (e.g. alice.bsky.social)" : "Bot Token"}</Label>
            <Input
              data-testid="custom-auth-field1"
              value={f1}
              onChange={(e) => setF1(e.target.value)}
              placeholder={isBluesky ? "alice.bsky.social" : "123456:AAF...........XYZ"}
              type={isBluesky ? "text" : "password"}
            />
          </div>
          <div>
            <Label>{isBluesky ? "App Password" : "Chat ID (e.g. @channelname or -100…)"}</Label>
            <Input
              data-testid="custom-auth-field2"
              value={f2}
              onChange={(e) => setF2(e.target.value)}
              placeholder={isBluesky ? "xxxx-xxxx-xxxx-xxxx" : "@your_channel"}
              type={isBluesky ? "password" : "text"}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="custom-auth-submit"
            disabled={connect.isPending || !f1.trim() || !f2.trim()}
            onClick={() => connect.mutate()}
            className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
          >
            {connect.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Single card ---------------- */

const PlatformCard: React.FC<{
  item: CatalogItem;
  onConnect: () => void;
  onDisconnect: (accountId: string) => void;
  disconnectPending: boolean;
  atLimit: boolean;
}> = ({ item, onConnect, onDisconnect, disconnectPending, atLimit }) => {
  const Icon = PLATFORM_ICONS[item.platform] || PLATFORM_ICONS.default;
  const disabled = item.status !== "live";
  return (
    <div
      className={`sg-card p-5 flex flex-col ${disabled ? "opacity-80" : ""}`}
      data-testid={`card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`h-11 w-11 rounded-lg ${Icon.bg} flex items-center justify-center shrink-0`}>
            <Icon.icon className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-base font-semibold tracking-tight truncate" data-testid={`card-title-${item.id}`}>
              {item.title}
            </div>
            <div className="text-[11px] text-[color:var(--sg-muted)] uppercase tracking-wider truncate">
              {item.account_type}
            </div>
          </div>
        </div>
        <span className={`sg-chip ${STATUS_BADGE[item.status]}`}>
          {item.status === "live" ? "Live" : item.status === "coming_soon" ? "Coming soon" : "Deprecated"}
        </span>
      </div>

      <p className="text-xs text-[color:var(--sg-muted)] mt-3 leading-relaxed line-clamp-3">
        {item.description}
      </p>
      {item.status === "deprecated" && item.reason && (
        <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
          {item.reason}
        </div>
      )}

      {/* Connected accounts list */}
      {item.connected_accounts.length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`accounts-${item.id}`}>
          {item.connected_accounts.map((a) => (
            <div
              key={a.account_id}
              className="flex items-center gap-2 border border-[color:var(--sg-border)] rounded-md p-2"
              data-testid={`account-${a.account_id}`}
            >
              {a.picture_url ? (
                <img src={a.picture_url} alt="" loading="lazy" decoding="async" className="h-8 w-8 rounded-full object-cover" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold">
                  {(a.display_name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold truncate">{a.display_name}</div>
                <div className="text-[10px] text-[color:var(--sg-muted)] truncate">
                  {a.handle ? `${a.handle} · ` : ""}Connected {new Date(a.connected_at).toLocaleDateString()}
                </div>
                {a.last_error && (
                  <div className="text-[10px] text-rose-700 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {a.last_error}
                  </div>
                )}
                {a.needs_reconnect && (
                  <div
                    className="mt-1 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 flex items-start gap-1.5"
                    data-testid={`needs-reconnect-${a.account_id}`}
                  >
                    <AlertTriangle className="h-3 w-3 mt-[1px] shrink-0" />
                    <span>
                      Missing <code className="font-mono">youtube.force-ssl</code> scope — replies are blocked.{" "}
                      <button
                        onClick={onConnect}
                        className="underline font-semibold hover:text-amber-900"
                        data-testid={`reconnect-cta-${a.account_id}`}
                      >
                        Reconnect
                      </button>{" "}
                      to grant it.
                    </span>
                  </div>
                )}
              </div>
              <button
                title="Reconnect"
                className="h-7 w-7 flex items-center justify-center rounded hover:bg-slate-100"
                onClick={onConnect}
                data-testid={`reconnect-${a.account_id}`}
              >
                <RefreshCw className="h-3.5 w-3.5 text-slate-600" />
              </button>
              <button
                title="Disconnect"
                className="h-7 w-7 flex items-center justify-center rounded hover:bg-rose-50 text-rose-600"
                onClick={() => onDisconnect(a.account_id)}
                disabled={disconnectPending}
                data-testid={`disconnect-${a.account_id}`}
              >
                {disconnectPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </button>
            </div>
          ))}
          {item.platform === "youtube" && item.connected_accounts.map((a) => (
            a.is_oauth ? <SyncControls key={"sc-" + a.account_id} accountId={a.account_id} /> : null
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        {disabled ? (
          <Button
            variant="outline"
            disabled
            className="w-full"
            data-testid={`btn-${item.id}`}
          >
            <Lock className="h-3.5 w-3.5 mr-1.5" />
            {item.status === "deprecated" ? "Not available" : "Coming soon"}
          </Button>
        ) : (
          <Button
            onClick={onConnect}
            disabled={atLimit}
            data-testid={`btn-${item.id}`}
            className="w-full bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
          >
            {atLimit ? (
              <>
                <Lock className="h-3.5 w-3.5 mr-1.5" /> Limit reached
              </>
            ) : (
              <>
                {item.connected_count > 0 ? <Plus className="h-3.5 w-3.5 mr-1.5" /> : null}
                {item.connected_count > 0 ? `Add another ${item.title.split(" ").slice(-1)[0]}` : item.cta}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
